'use client'

import { ChevronDown, GripVertical } from 'lucide-react'
import { useRef, useState, type PointerEvent } from 'react'

import { minimizeVoisoSoftphone, saveVoisoPanelPosition, useVoisoPanel, type Position } from './voisoPanel'
import { Button, cn } from '@fp/ui-kit'
import { VOISO_EMBED_URL } from '@lib/constants'
import { commonIconProps, iconProps } from '@lib/iconProps'

/** Keeps the drag bar on screen even if the stored position outgrows a smaller window. */
const clampedStyle = ({ x, y }: Position): React.CSSProperties => ({
  left: `max(0px, min(${x}px, 100vw - 20rem))`,
  top: `max(0px, min(${y}px, 100vh - 2rem))`,
  right: 'auto',
  bottom: 'auto',
})

/**
 * Embedded Voiso softphone: once connected, the iframe stays mounted for the whole session, because
 * unmounting, moving in the DOM or re-keying it reloads Voiso and drops a live call. Dragging only changes
 * the panel's CSS position, and minimizing only fades it out in place: a frame that stays in the viewport
 * isn't throttled by Chrome, so it keeps ringing.
 */
export const VoisoSoftphone = () => {
  const { connected, expanded, pos } = useVoisoPanel()

  // Live position while dragging; the store takes over once the drag is released.
  const [dragPos, setDragPos] = useState<Position | null>(null)
  const dragOffset = useRef<{ dx: number; dy: number } | null>(null)
  const panelPos = dragPos ?? pos

  const startDrag = (e: PointerEvent<HTMLElement>) => {
    const rect = e.currentTarget.closest('#voiso-softphone')?.getBoundingClientRect()
    if (!rect) return
    dragOffset.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top }
    e.currentTarget.setPointerCapture(e.pointerId)
  }

  const onDrag = (e: PointerEvent<HTMLElement>) => {
    if (!dragOffset.current) return
    setDragPos({ x: e.clientX - dragOffset.current.dx, y: e.clientY - dragOffset.current.dy })
  }

  const endDrag = (e: PointerEvent<HTMLElement>) => {
    const offset = dragOffset.current
    dragOffset.current = null
    e.currentTarget.releasePointerCapture(e.pointerId)
    if (offset) saveVoisoPanelPosition({ x: e.clientX - offset.dx, y: e.clientY - offset.dy })
    setDragPos(null)
  }

  if (!connected) return null

  return (
    <div
      id='voiso-softphone'
      style={panelPos ? clampedStyle(panelPos) : undefined}
      // z-60 sits above Dialog overlays (z-50), so the phone stays usable while a CRM dialog is open
      className={cn(
        'fixed top-20 right-4 z-[60] w-80 overflow-hidden rounded-lg border border-input bg-card shadow-lg transition-opacity duration-200',
        expanded ? 'pointer-events-auto' : 'pointer-events-none opacity-0',
        dragPos && 'select-none',
      )}
    >
      <div
        onPointerDown={startDrag}
        onPointerMove={onDrag}
        onPointerUp={endDrag}
        className='flex cursor-grab items-center justify-between border-b border-input pl-2 text-xs font-semibold active:cursor-grabbing'
      >
        <span className='flex items-center gap-1.5'>
          <GripVertical {...iconProps.xs} className='size-3.5 text-muted-foreground' />
          Voiso
        </span>
        <Button
          variant='ghost'
          size='sm'
          aria-label='Minimize Voiso'
          onPointerDown={(e) => e.stopPropagation()}
          onClick={minimizeVoisoSoftphone}
        >
          <ChevronDown {...commonIconProps} />
        </Button>
      </div>
      <iframe
        src={VOISO_EMBED_URL}
        title='Voiso softphone'
        allow='microphone; autoplay'
        // 320×600 = Voiso's own compact Agent Workspace window; no pointer events while dragging over it
        className={cn('block h-[min(600px,calc(100vh-8rem))] w-full border-0', dragPos && 'pointer-events-none')}
      />
    </div>
  )
}
